import { useId, type ReactElement, type ReactNode } from 'react';

import { useEscapeKey } from '../hooks/useEscapeKey.js';

import { pillButton } from './pillButton.js';

type RoomModalProps = {
  readonly title: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
};

/**
 * A deliberately obstructing, full-bleed panel: it is meant to be turned
 * towards a room, so it hides the console rather than floating over it.
 */
export const RoomModal = ({ title, onClose, children }: RoomModalProps): ReactElement => {
  const titleId = useId();
  useEscapeKey(onClose);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="bg-canvas fixed inset-0 z-40 flex flex-col overflow-y-auto"
    >
      <div className="border-subtle flex shrink-0 items-center justify-between gap-4 border-b px-6 py-4">
        <h2 id={titleId} className="font-display text-content min-w-0 truncate text-xl">
          {title}
        </h2>
        <button type="button" onClick={onClose} className={`${pillButton('quiet')} shrink-0`}>
          Close
        </button>
      </div>

      <div className="flex flex-1 flex-col items-center justify-center gap-8 px-6 py-10 text-center">
        {children}
      </div>

      <p className="text-muted shrink-0 pb-6 text-center text-xs">Press Escape to close</p>
    </div>
  );
};
